<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full text-[#414141] font-sans">
      <h2 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
        Fundraising Talking Points
      </h2>

      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
        <p>Loading data...</p>
      </div>
      <div
        v-else-if="!authStore.user?.organization_id"
        class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
      >
        <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
        <button
          @click="authStore.fetchUserData"
          class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
        >
          Retry Loading Organization Data
        </button>
      </div>
      <div v-else>
        <div class="w-full">
          <div class="overflow-auto pb-16 scroll-smooth" ref="contentWindow">
            <div class="mt-4">
              <div class="flex items-start text-gray-700 mt-2 font-bold">
                <span class="">1.</span>
                <p class="ml-1">Thank Donor for Meeting</p>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">2.</span>
                <p class="ml-1">
                  <span class="font-bold">Mission: </span>
                  {{ pitchInfo?.mission }}
                </p>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">3.</span>
                <p class="ml-1">
                  <span class="font-bold">Aspirational Vision: </span>
                  {{ pitchInfo.vision }}
                </p>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">4.</span>
                <p class="ml-1">
                  <span class="font-bold">Annual Operating Budget: </span>
                  ${{ formatNumber(pitchInfo.organization_budget) }}
                </p>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">5.</span>
                <p class="ml-1">
                  <span class="font-bold">Annual Philanthropic Revenue: </span>
                  ${{ formatNumber(pitchInfo.philanthropic_total) }}
                </p>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <p class="font-bold">6. Core Annual Programs</p>
                <div class="ml-3">
                  <ol type="a">
                    <li
                      class="mx-2"
                      style="text-transform: capitalize"
                      v-for="(project, index) in pitchInfo.core_programs"
                      :key="index"
                    >
                      {{ String.fromCharCode(index + 97) }}.
                      {{ project?.program_name }}
                    </li>
                  </ol>
                </div>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <p class="font-bold">7. Funding New Projects</p>
                <div class="ml-3">
                  <ol type="a">
                    <li
                      class="mx-2"
                      style="text-transform: capitalize"
                      v-for="(project, index) in pitchInfo.projects"
                      :key="index"
                    >
                      {{ String.fromCharCode(index + 97) }}.
                      {{ project?.project_name }}
                    </li>
                  </ol>
                </div>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">8. 3 Year Fundraising Goal</span>
                <p class="ml-1">
                  <span class="">:
                    ${{ formatNumber(pitchInfo.total_fundraising_goal_3y) }}
                  </span>
                </p>
              </div>

              <div
                class="flex items-start text-gray-700 mt-2"
                v-if="pitchInfo.recognition_range !== null"
              >
                <span class="font-bold">9. Recognition Range </span>
                <p class="ml-1">
                  <span class="">: Up to ${{ formatNumber(pitchInfo.recognition_range) }}
                  </span>
                </p>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <span class="font-bold">10. Solicitation </span>
                <div class="ml-3">
                  <ol type="a">
                    <li class="mx-2">
                      a. How do you want the donor/prospect to support your
                      organization?
                    </li>
                    <li class="mx-2">b. What is the specific ask?</li>
                  </ol>
                </div>
              </div>

              <div class="items-start text-gray-700 mt-2">
                <span class="font-bold">11. Gifts Accepted </span>
                <div class="ml-3">
                  <ol type="a">
                    <li
                      class="mx-2"
                      v-for="(waysObj, idx) in (Array.isArray(pitchInfo?.ways_to_give) ? 
                        pitchInfo.ways_to_give.filter(obj => obj.isSelected === true) : [])"
                      :key="idx"
                    >
                      {{ waysObj.label }}
                    </li>
                    <li
                      class="mx-2"
                      v-for="(subObj, idx) in (Array.isArray(pitchInfo?.ways_to_give) && pitchInfo.ways_to_give[3]?.subOption ? 
                        pitchInfo.ways_to_give[3].subOption.filter(obj => obj.isSelected === true) : [])"
                      :key="idx"
                    >
                      Planned Gifts - {{ subObj.label }}
                    </li>
                  </ol>
                </div>
              </div>

              <div class="flex items-start text-gray-700 mt-2">
                <span class="font-bold">12.</span>
                <p class="ml-1 font-bold">Thank you for Meeting</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import global from "@/global";
import { useStepValidation } from "@/composables/useStepValidation";
const isLoading = ref(true);
const errorMessage = ref("");
const contentWindow = ref(null);
const pitchInfo = ref({});

// Fetching the auth store
const authStore = useAuthStore();

// Add validation function for the step
const validateStep = async () => {
  // Since this is a display-only page, we can always return true to allow navigation
  return true;
};

// Use the step validation composable
useStepValidation({
  initialValidState: true,
  validateFn: validateStep,
});

// Format number helper function
function formatNumber(value) {
  if (!value) return '0';
  return new Intl.NumberFormat('en-US').format(value);
}

// Fetching pitch training data
async function fetchPitchTrainingData() {
  try {
    const res = await fetch(`${global.API_URL}/pitch-training`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    
    const data = await res.json();
    pitchInfo.value = data.data;
  } catch (error) {
    console.error("Failed to fetch pitch training data:", error);
    errorMessage.value = "Failed to load pitch training data. Please try refreshing the page.";
  }
}

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    await authStore.fetchUserData();

    if (!authStore.user?.organization_id) {
      errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
      console.error("No organization ID available");
      return;
    }

    await fetchPitchTrainingData();

  } catch (error) {
    console.error("Error during component initialization:", error);
    errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.answer-err-enter-active,
.answer-err-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.answer-err-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.answer-err-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style> 